import {
  AddIcon,
  Button,
  CurrencyInput,
  SubtractIcon,
} from '@paypalcorp/pp-react'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { interpolate } from 'ppbl/utils/string-helper'
import React, { useContext, useCallback, useEffect, useState } from 'react'
import { toCommaSeparatedNumber } from '../__utils__'
import { OfferContext } from './offerContext'
import { merge } from 'lodash-es'
import { trackClick } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'

/**
 * This is a functional component that takes input of desired loan amount
 * it also validates input against min and max pffer amount limiy
 * @returns {JSX.Element} - A JSX element
 */
const OfferInputV2: React.FC = () => {
  const {
    loanAmount,
    updateLoanAmount,
    offerMaxAmount,
    offerMinAmount,
    disableContinue,
    fptiData,
    applicationId,
  } = useContext(OfferContext)
  const [inputError, setInputError] = useState(false)
  const offerInputHelperText = `$${toCommaSeparatedNumber(
    offerMinAmount,
    0,
  )} -  $${toCommaSeparatedNumber(offerMaxAmount)}`
  const deltaValue = 500

  const determineMinusState = useCallback(() => {
    return Number(loanAmount) <= Number(offerMinAmount)
  }, [loanAmount, offerMinAmount])

  const determinePlusState = useCallback(() => {
    return Number(loanAmount) >= Number(offerMaxAmount)
  }, [loanAmount, offerMaxAmount])

  const [isMinusDisabled, setMinusDiable] = useState(determineMinusState())
  const [isPlusDisabled, setPlusDiable] = useState(determinePlusState())

  // checks the validity of input box
  const isValidInput = (inputValue) => {
    if (
      Number(inputValue) >= Number(offerMinAmount) &&
      Number(inputValue) <= Number(offerMaxAmount)
    ) {
      return true
    }
    return false
  }

  const handleDecrement = () => {
    trackClick(
      merge(fptiData, {
        component: 'offerAmount_decrement_button',
        link: 'offerAmount_decrement_button',
        applicationId,
        event_name: FPTI_EVENTS.CLICK.OFFER.AMOUNT.DECREMENT,
      }),
    )
    if (!isMinusDisabled) {
      const validDecrement = () => {
        if (isValidInput(Number(loanAmount) - deltaValue)) {
          return Number(loanAmount) - deltaValue
        }
        if (isValidInput(loanAmount)) {
          return offerMinAmount
        }
        return offerMaxAmount
      }
      updateLoanAmount(String(validDecrement()))
    }
  }

  const handleIncrement = () => {
    trackClick(
      merge(fptiData, {
        component: 'offerAmount_increment_button',
        link: 'offerAmount_increment_button',
        applicationId,
        event_name: FPTI_EVENTS.CLICK.OFFER.AMOUNT.INCREMENT,
      }),
    )
    if (!isPlusDisabled) {
      const validIncrement = () => {
        if (isValidInput(Number(loanAmount) + deltaValue)) {
          return Number(loanAmount) + deltaValue
        }
        if (isValidInput(loanAmount)) {
          return offerMaxAmount
        }
        return offerMinAmount
      }
      updateLoanAmount(String(validIncrement()))
    }
  }

  useEffect(() => {
    setMinusDiable(determineMinusState())
    setPlusDiable(determinePlusState())
  }, [determineMinusState, determinePlusState, loanAmount])

  /**
   * This function is triggered on user input and validats the unmasked input
   * throws error if the value is out of bound else sets input
   */

  const handleAccept = (
    _value: string,
    unmaskedValue: string,
    _typedValue: string,
  ) => {
    const validInput = isValidInput(unmaskedValue)
    setInputError(!validInput)
    disableContinue(!validInput)
    updateLoanAmount(unmaskedValue)
  }

  const handleTrackInput = (event) => {
    if (Number(loanAmount) < Number(offerMinAmount)) {
      updateLoanAmount(String(offerMinAmount))
    } else if (Number(loanAmount) > Number(offerMaxAmount)) {
      updateLoanAmount(String(offerMaxAmount))
    }
    trackClick(
      merge(fptiData, {
        component: `offerInput_V2_${event.target.value}`,
        link: `offerInput_V2_${event.target.value}`,
        applicationId,
        event_name: FPTI_EVENTS.CLICK.OFFER.AMOUNT.V2_INPUT,
      }),
    )
  }

  return (
    <div className="offerInputContainerV2">
      <div className="currencyButtonColV2">
        <Button
          type="button"
          aria-label="Decrement Loan Amount"
          disabled={isMinusDisabled}
          onClick={handleDecrement}
          className="actionButtonV2"
          data-testid="decrement-button"
        >
          <SubtractIcon size="sm" className="minusIconV2" />
        </Button>
      </div>
      <div className="currencyInputColV2">
        <CurrencyInput
          style={{ textAlign: 'center' }}
          className="currencyInputV2"
          name="offer-amount-input"
          aria-label={getLocalizedText('offer-page.offerInput.headerText')}
          onAccept={handleAccept}
          onBlur={handleTrackInput}
          value={loanAmount}
          helperText={offerInputHelperText}
          errorText={
            inputError &&
            interpolate(
              getLocalizedText('offer-page.offerInput.inputErrorText'),
              {
                offerInputHelperText,
              },
            )
          }
          maskOptions={{
            mask: '$ num',
            blocks: {
              num: {
                mask: Number,
                thousandsSeparator: ',',
                padFractionalZeros: false,
              },
            },
            lazy: false,
          }}
        />
      </div>
      <div className="currencyButtonColV2">
        <Button
          type="button"
          aria-label="Increment Loan Amount"
          disabled={isPlusDisabled}
          onClick={handleIncrement}
          className="actionButtonV2"
          data-testid="increment-button"
        >
          <AddIcon size="sm" className="plusIconV2" />
        </Button>
      </div>
    </div>
  )
}

export default OfferInputV2
